import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Observable, tap, map } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ILogin,
  ILoginData,
  IRegister,
  IResponse,
  IUserData,
  LoginFlowDestination,
} from '../core.interface';
import { Router } from '@angular/router';
import { Core } from './core';
import { markLoggedInOnce } from '../welcome-tour/previous-office-url';

const DEMO_SESSION_KEY = 'ggm-demo-session';

export function markDemoSession(): void {
  sessionStorage.setItem(DEMO_SESSION_KEY, '1');
}

export function isDemoSession(): boolean {
  return sessionStorage.getItem(DEMO_SESSION_KEY) === '1';
}
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private $core = inject(Core);

  public _accessToken = signal<string | null>(sessionStorage.getItem('access_token'));
  public _refreshToken = signal<string | null>(sessionStorage.getItem('refresh_token'));
  public user = signal<ILoginData | null>(null);
  public userData = signal<IUserData | null>(null);

  public signin(userdata: ILogin): Observable<IResponse<ILoginData>> {
    return this.http.post<IResponse<ILoginData>>(`${environment.apiUrl}/auth/login`, userdata).pipe(
      tap((response: IResponse<ILoginData>) => {
        this.clearSession();
        this.setAuthToken(response);
        this.setUserData(JSON.stringify(response.data));
      }),
    );
  }

  public ssoExchange(code: string): Observable<IResponse<ILoginData>> {
    return this.http
      .post<IResponse<ILoginData>>(`${environment.apiUrl}/auth/sso-exchange`, { code })
      .pipe(
        tap((response: IResponse<ILoginData>) => {
          this.clearSession();
          this.setAuthToken(response);
          this.setUserData(JSON.stringify(response.data));
        }),
      );
  }

  public async completeLoginFlow(): Promise<LoginFlowDestination> {
    this.hydrateStoredUser();
    if (this.user()?.expiredPassword === true) {
      return 'CHANGE_PASSWORD';
    }

    await this.isUserDataLoaded();
    if (this.userData()?.hasExpiredPassword === true) {
      return 'CHANGE_PASSWORD';
    }

    const buildings = await this.$core.fetchBuildings();
    return buildings.length === 1 ? 'LOADING' : 'PROPIEDADES';
  }

  public register(data: IRegister): Observable<unknown> {
    return this.http.post(`${environment.apiUrl}/auth/tenant/register`, {
      ...data,
      appId: environment.appID,
    });
  }

  public forgotPassword(email: string): Observable<any> {
    return this.http.post<Response>(`${environment.apiUrl}/auth/forgot-password`, {
      email: email,
      appId: environment.appID,
    });
  }

  public async changePassword(data: any): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.post<Response>(`${environment.apiUrl}/auth/change-password`, data),
      );
      this.clearExpiredPasswordFlags();
      return true;
    } catch (error) {
      console.error('No se pudo cambiar la contraseña:', error);
      return false;
    }
  }

  /** Tras cambiar la contraseña, invalida flags caducados y fuerza refresco de /auth/me. */
  private clearExpiredPasswordFlags(): void {
    const currentUser = this.user();
    if (currentUser) {
      const updatedUser = { ...currentUser, expiredPassword: false };
      this.user.set(updatedUser);
      sessionStorage.setItem('ggm-user', JSON.stringify(updatedUser));
    }

    // Fuerza recarga en el próximo isUserDataLoaded()
    this.userData.set(null);
  }

  public async isUserDataLoaded(): Promise<boolean> {
    this.hydrateStoredUser();
    if (this.user()?.expiredPassword === true) {
      this.navigateToChangePassword();
      return false;
    }

    if (this.userData() === null) {
      try {
        const response = await firstValueFrom(
          this.http.get<{ data: IUserData }>(`${environment.apiUrl}/auth/me`),
        );

        this.userData.set(response.data);
      } catch (error) {
        console.error('Get user data error:', error);
        this.userData.set(null);
        return false;
      }
    }

    if (this.userData()?.hasExpiredPassword === true) {
      this.navigateToChangePassword();
      return false;
    }

    return true;
  }

  public hasExpiredPassword(): boolean {
    this.hydrateStoredUser();
    return this.user()?.expiredPassword === true || this.userData()?.hasExpiredPassword === true;
  }

  private hydrateStoredUser(): void {
    if (this.user() !== null) {
      return;
    }
    const raw = sessionStorage.getItem('ggm-user');
    if (!raw) {
      return;
    }
    try {
      this.user.set(JSON.parse(raw) as ILoginData);
    } catch {
      this.user.set(null);
    }
  }

  private navigateToChangePassword(): void {
    const alreadyOnChangePassword =
      this.router.url.startsWith('/change-password') ||
      (this.router.url.startsWith('/login') &&
        this.router.parseUrl(this.router.url).queryParams['view'] === 'CHANGE_PASSWORD');

    if (alreadyOnChangePassword) {
      return;
    }

    void this.router.navigate(['/', 'change-password'], { replaceUrl: true });
  }

  public setAuthToken(value: IResponse<ILoginData>): void {
    markLoggedInOnce();
    this._accessToken.set(value.data.accessToken);
    this._refreshToken.set(value.data.refreshToken);
    sessionStorage.setItem('ggm-user', JSON.stringify(value.data));
    sessionStorage.setItem('ggm-int-tkn', value.data.accessToken);
    sessionStorage.setItem('ggm-int-ref', value.data.refreshToken);
  }

  public setRefreshToken(refreshToken: string): void {
    this._refreshToken.set(refreshToken);
  }

  public setUserData(value: string): any {
    this.user.set(JSON.parse(value));
  }

  public getAuthToken(): string | null {
    const token = sessionStorage.getItem('ggm-int-tkn');
    if (!!token) {
      return token;
    }
    return null;
  }

  public getRefreshToken(): string | null {
    const refreshToken = sessionStorage.getItem('ggm-int-ref');
    if (!!refreshToken) {
      return refreshToken;
    }
    const userToken = JSON.parse(sessionStorage.getItem('ggm-user')!).refreshToken;
    if (!!userToken) {
      return userToken;
    }

    return null;
  }

  public refreshToken(): Observable<string> {
    return this.http
      .post<IResponse<ILoginData>>(`${environment.apiUrl}/auth/refresh`, {
        accessToken: this.getAuthToken(),
        refreshToken: this.getRefreshToken(),
      })
      .pipe(
        tap((resp) => {
          this.setAuthToken(resp);
        }),
        map((resp) => resp.data.accessToken),
      );
  }

  public clearSession(): void {
    sessionStorage.removeItem('ggm-int-tkn');
    sessionStorage.removeItem('ggm-int-ref');
    sessionStorage.removeItem('ggm-user');
    sessionStorage.removeItem('tenantId');
    sessionStorage.removeItem(DEMO_SESSION_KEY);
    this._accessToken.set(null);
    this._refreshToken.set(null);
    this.user.set(null);
    this.userData.set(null);
  }

  public removeAuthToken(): void {
    this.clearSession();
    this.router.navigate(['/', 'login']);
  }
}
